import { buildDeliveryAvailability } from '@libs/analytics';
import type { DeliveryMethod, DeliveryMethodType, VariantDeliveryMethods } from '@libs/common-types';

import type {
    AddToCartBaseItem,
    AddToCartCrawlEvent,
    AddToCartEcommerce,
    AddToCartPrecrawlEvent,
    StockAvailability,
} from '@/shared/analytics';

import { buildFBTDeliveryAvailability } from './buildFBTDeliveryAvailability';

type FBTAddToCartBaseEvent = {
    event: 'add_to_cart';
    ecommerce: Omit<AddToCartEcommerce<AddToCartBaseItem>, 'items'> & {
        items: AddToCartBaseItem[];
    };
};

type DeliveryAvailabilityParams = {
    deliveryMethods: { [K in DeliveryMethodType]?: DeliveryMethod };
    tailoredVariant: Maybe<VariantDeliveryMethods>;
    purchaseOption: 'one-time' | 'autoship';
    isCustomPharmacyOOS: boolean;
};

type FBTCrawlParams = {
    mainProductSku: string;
    deliveryParams: DeliveryAvailabilityParams;
};

type PrecrawlEnrichmentData = {
    autoshipFlag: 'precrawl';
    stockAvailability: Nullable<StockAvailability>;
};

type CrawlEnrichmentData = {
    autoshipFlag: 'crawl';
    fbtParams: FBTCrawlParams;
};

type FBTEnrichmentData = PrecrawlEnrichmentData | CrawlEnrichmentData;

/**
 * Enriches FBT add-to-cart events with per-item delivery availability.
 *
 * - Main PDP product (matching mainProductSku): receives full delivery data (BOPIS, STH, SDD)
 * - Additional FBT products: receive BOPIS only (STH/SDD set to "not available")
 */
export function enrichFBTAddToCartEvent(
    event: FBTAddToCartBaseEvent,
    enrichmentData: FBTEnrichmentData
): AddToCartPrecrawlEvent | AddToCartCrawlEvent {
    if (enrichmentData.autoshipFlag === 'precrawl') {
        const items = event.ecommerce.items.map(item => ({
            ...item,
            autoship_stock_availability: enrichmentData.stockAvailability,
        }));

        return {
            event: 'add_to_cart',
            ecommerce: {
                ...event.ecommerce,
                items,
            },
        } satisfies AddToCartPrecrawlEvent;
    }

    const { mainProductSku, deliveryParams } = enrichmentData.fbtParams;

    const items = event.ecommerce.items.map(item => {
        const isMainProduct = item.item_variant === mainProductSku;
        const deliveryData = isMainProduct
            ? buildDeliveryAvailability(deliveryParams)
            : buildFBTDeliveryAvailability(deliveryParams);

        return {
            ...item,
            ...deliveryData,
        };
    });

    return {
        event: 'add_to_cart',
        ecommerce: {
            ...event.ecommerce,
            items,
        },
    } satisfies AddToCartCrawlEvent;
}

export type { FBTAddToCartBaseEvent, FBTEnrichmentData };
